import { useCallback, useEffect, useState } from 'react'
import { StructuredSummaryResult, StructuredSummaryResultType } from '@zocks-communications/client-sdk-js'
import { ResultSimilarity, ResultSuitable } from '../../../../../models/AISystemPresetResult'
import { TrackEvent, useTracking } from '../../../../../apps/professional-app/fa/common/tracking'
import { useExecuteSystemPresetMutation } from '../../../../../store/ai/aiExecute'
import { useAppSelector } from '../../../../../store/hooks'
import { selectAccountId } from '../../../../../store/auth/authSlice'
import { AiFeaturePreset } from '../../../../../models/AiFeaturePreset'
import { TemplateInputParameters } from '../../../../../models/AiFeature'

export const useGenerateTemplate = (preset: AiFeaturePreset, inputParameters: TemplateInputParameters) => {
  const { example_1, example_2 } = inputParameters

  const accountId = useAppSelector(selectAccountId)

  const { tracked } = useTracking()

  const [executeSystemPreset] = useExecuteSystemPresetMutation()

  const [generateIsLoading, setGenerateIsLoading] = useState<boolean>(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [generatingEnabled, setGeneratingEnabled] = useState<boolean>(true)
  const [generatedTemplate, setGeneratedTemplate] = useState<string | null>(null)

  useEffect(() => {
    if (!example_1?.trim()) {
      setGeneratingEnabled(false)
    } else {
      setGeneratingEnabled(true)
    }
  }, [example_1])

  const startGenerating = () => {
    setGenerateIsLoading(true)
    setGeneratingEnabled(false)
    setErrorMessage(null)
    setGeneratedTemplate(null)
  }

  const endGenerating = () => {
    setGenerateIsLoading(false)
    setGeneratingEnabled(true)
  }

  const _getSimilarity = useCallback(async () => {
    try {
      const similaritySystemPresetName = preset.inputParametersSchema?.properties?.template.similaritySystemPresetName
      const result = await executeSystemPreset({
        accountId: accountId,
        payload: {
          systemPresetName: similaritySystemPresetName,
          context: {
            example_1,
            example_2,
          },
        },
      })
      if (result.data) {
        const endResult = (result.data[0].result as StructuredSummaryResult).result as {
          [key: string]: StructuredSummaryResultType
        }
        return {
          isSimilar: endResult.isSimilar,
          explanation: endResult.explanation,
        } as ResultSimilarity
      }
    } catch (e: any) {
      if ('errors' in e) {
        setErrorMessage(e.errors.map((err: any) => err.message).join(', '))
      } else {
        setErrorMessage(JSON.stringify(e))
      }
      return
    }
  }, [accountId, example_1, example_2])

  const _getSuitability = useCallback(async () => {
    try {
      const suitabilitySystemPresetName = preset.inputParametersSchema?.properties?.template.suitabilitySystemPresetName
      const example = example_1 !== '' ? example_1 : example_2
      const result = await executeSystemPreset({
        accountId: accountId,
        payload: {
          systemPresetName: suitabilitySystemPresetName,
          context: {
            example,
          },
        },
      })
      if (result.data) {
        const endResult = (result.data[0].result as StructuredSummaryResult).result as {
          [key: string]: StructuredSummaryResultType
        }
        return {
          isSuitable: endResult.isSuitable,
          explanation: endResult.explanation,
        } as ResultSuitable
      }
    } catch (e: any) {
      if ('errors' in e) {
        setErrorMessage(e.errors.map((err: any) => err.message).join(', '))
      } else {
        setErrorMessage(JSON.stringify(e))
      }
      return
    }
  }, [accountId, example_1, example_2])

  const _getTemplate = useCallback(async () => {
    try {
      const generatorSystemPresetName = preset.inputParametersSchema?.properties?.template.generatorSystemPresetName
      const result = await executeSystemPreset({
        accountId: accountId,
        payload: {
          systemPresetName: generatorSystemPresetName,
          context: {
            example_1,
            example_2,
          },
        },
      })
      if (result.data) {
        return (result.data[0].result as StructuredSummaryResult).result as string
      }
    } catch (e: any) {
      if ('errors' in e) {
        setErrorMessage(e.errors.map((err: any) => err.message).join(', '))
      } else {
        setErrorMessage(JSON.stringify(e))
      }
      return
    }
  }, [accountId, example_1, example_2])

  const _calculateTemplate = useCallback(async () => {
    const suitability = await _getSuitability()
    if (suitability) {
      if (suitability?.isSuitable) {
        return await _getTemplate()
      } else {
        setErrorMessage(suitability?.explanation + 'Try again with other example(s) or modify our default template by hand.')
      }
    }
  }, [_getSuitability, _getTemplate])

  const _runSystemPresets = useCallback(async () => {
    startGenerating()
    try {
      const onlyExample = example_2 === ''
      if (!onlyExample) {
        const similarity = await _getSimilarity()
        if (similarity?.isSimilar) {
          const template = await _calculateTemplate()
          setGeneratedTemplate(template ?? null)
          return template
        } else {
          setErrorMessage(similarity?.explanation + 'Please select only one example to use or provide new ones.')
        }
      } else {
        const template = await _calculateTemplate()
        setGeneratedTemplate(template ?? null)
        return template
      }
    } finally {
      endGenerating()
    }
  }, [example_2, _getSimilarity, _calculateTemplate])

  const generateTemplate = tracked(_runSystemPresets, {
    eventName: TrackEvent.AI_SYSTEM_PRESET_RUN,
    eventDetails: { presetId: preset.id },
  })

  const clearGeneratedTemplate = useCallback(() => {
    setGeneratedTemplate(null)
  }, [])

  return { generateTemplate, generatingEnabled, generateIsLoading, errorMessage, generatedTemplate, clearGeneratedTemplate }
}
